import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search as SearchIcon,
  Sparkles,
  Languages,
  Award,
  Recycle,
  ArrowLeft,
  Megaphone,
  FolderOpen,
} from "lucide-react";
import { toast } from "react-hot-toast";
import campaignsService, { CampaignFolder, SearchHit } from "../../services/campaigns.service";
import { Dropdown, DropdownOption } from "../users/ui";
import Loader from "../../widgets/Loader";

const LANG_LABELS: Record<string, string> = {
  en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", ml: "Malayalam",
  mr: "Marathi", bn: "Bengali", gu: "Gujarati", kn: "Kannada",
};

const TARGET_LANG_OPTIONS: DropdownOption[] = Object.entries(LANG_LABELS).map(
  ([value, label]) => ({ value, label })
);

// All four prompts intentionally describe the SAME need (a credit-card launch
// campaign) in different languages — that's the whole point: any of them
// should retrieve the same Mumbai (Hindi) source campaign.
const SAMPLE_PROMPTS: { lang: string; text: string }[] = [
  { lang: "ta", text: "புதிய கிரெடிட் கார்டு வெளியீட்டு கேம்பெயின் வேண்டும்" },
  { lang: "hi", text: "क्रेडिट कार्ड लॉन्च कैम्पेन के लिए कंटेंट चाहिए" },
  { lang: "en", text: "I need a credit card launch campaign for young urban professionals" },
  { lang: "ml", text: "പുതിയ ക്രെഡിറ്റ് കാർഡ് ലോഞ്ച് കാമ്പെയ്ൻ വേണം" },
];

function CampaignsSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get("folder") || "";
  const [folder, setFolder] = useState<CampaignFolder | null>(null);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState("ta");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!folderId) {
      // No folder in query — bounce back to the picker
      navigate("/campaigns", { replace: true });
      return;
    }
    (async () => {
      try {
        const folders = await campaignsService.listFolders();
        setFolder(folders.find((x) => x._id === folderId) || null);
      } catch (e) { /* non-fatal */ }
    })();
  }, [folderId, navigate]);

  const runSearch = async (q: string) => {
    if (!q.trim()) return;
    try {
      setIsSearching(true);
      setHasSearched(true);
      const res = await campaignsService.search(q, target, 6, folderId);
      setHits(res);
    } catch (e: any) {
      toast.error("Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => { e.preventDefault(); runSearch(query); };

  const useAsSource = (hit: SearchHit) => {
    navigate(`/campaigns/${hit.campaign._id}/workspace?target=${target}&q=${encodeURIComponent(query)}&folder=${folderId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
        <div className="px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(folderId ? `/campaigns?folder=${folderId}` : "/campaigns")}
            className="text-gray-500 hover:text-gray-800 p-1 rounded"
            type="button"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Megaphone className="w-5 h-5 text-blue-500" />
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2 truncate">
              New Draft — Cross-lingual search
            </h1>
            <p className="text-xs text-slate-600 flex items-center gap-1.5">
              {folder && (
                <>
                  <FolderOpen className="w-3 h-3 text-blue-400" />
                  <span className="font-medium text-gray-700">{folder.name}</span>
                  <span className="text-gray-300">·</span>
                </>
              )}
              <span>Search in any language within this folder. Find a source. Generate localized content.</span>
            </p>
          </div>
        </div>
      </div>

      {/* "How this works" — 3-step flow strip */}
      <div className="px-6 mb-3">
        <div className="bg-blue-50 border border-blue-100 rounded-md px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-gray-700">
          <span className="font-semibold text-blue-700 uppercase tracking-wider text-[10px]">How this works</span>
          <span className="hidden sm:inline text-gray-300">|</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">1</span>
              Type your need (any language)
            </span>
            <span className="text-gray-300">→</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">2</span>
              Pick a source from results
            </span>
            <span className="text-gray-300">→</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">3</span>
              Workspace generates a localized draft
            </span>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="px-6">
        <form
          onSubmit={onSubmit}
          className="bg-white border border-gray-200 rounded-md p-4"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Describe the campaign you want — in any language…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase text-gray-500">Target lang:</label>
              <Dropdown
                options={TARGET_LANG_OPTIONS}
                value={target}
                onChange={setTarget}
                className="w-40"
              />
            </div>
            <button
              type="submit"
              disabled={!query.trim() || isSearching}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              {isSearching ? "Searching…" : "Find Campaigns"}
            </button>
          </div>

          {/* Sample prompts */}
          {!hasSearched && (
            <div className="mt-4">
              <p className="text-[11px] text-gray-600 mb-2">
                <span className="font-semibold uppercase tracking-wider text-gray-500 mr-1.5">Try a sample prompt</span>
                — same ask in 4 languages. Click any one to see how cross-lingual search works.
              </p>
              <div className="flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setQuery(p.text); runSearch(p.text); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 border border-gray-200 hover:border-blue-300 rounded-md text-xs text-gray-700 transition-colors"
                  >
                    <Languages className="w-3 h-3" />
                    <span className="font-medium">{LANG_LABELS[p.lang]}:</span>
                    <span className="truncate max-w-[300px]">{p.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Results */}
      <div className="px-6 py-6">
        {!hasSearched && (
          <div className="bg-white border border-dashed border-gray-300 rounded-md p-10 text-center">
            <SearchIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-700 font-medium">Type a prompt above to find campaigns to reuse.</p>
            <p className="text-xs text-gray-500 mt-1.5 max-w-md mx-auto">
              We match across <strong>all languages</strong> in this folder — your Tamil prompt can find Hindi, English or Malayalam campaigns. Pick the best match as your starting source.
            </p>
          </div>
        )}

        {isSearching && (
          <div className="bg-white border border-gray-200 rounded-md p-12 text-center">
            <Loader />
            <p className="mt-3 text-sm text-gray-600">Searching across languages…</p>
          </div>
        )}

        {hasSearched && !isSearching && hits.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-md p-12 text-center">
            <p className="text-sm text-gray-500">No matching campaigns. Try a broader prompt.</p>
          </div>
        )}

        {hasSearched && !isSearching && hits.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{hits.length}</span> matching campaigns •
                Target language: <span className="font-medium text-gray-900">{LANG_LABELS[target]}</span>
              </p>
            </div>

            {hits.map((hit, idx) => (
              <div
                key={hit.campaign._id}
                className="bg-white border border-gray-200 rounded-md hover:border-blue-300 transition-colors p-4"
              >
                <div className="flex items-start gap-4">
                  {/* rank + score */}
                  <div className="shrink-0 flex flex-col items-center w-12">
                    <div className="text-2xl font-bold text-gray-300 leading-none">{idx + 1}</div>
                    <div className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded">
                      {Math.round(hit.score * 100)}%
                    </div>
                  </div>

                  {/* meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-gray-900">{hit.campaign.name}</h3>
                          {hit.campaign.is_gold && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold">
                              <Award className="w-3 h-3" /> GOLD
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium">
                            <Languages className="w-3 h-3" /> {hit.source_lang_label}
                          </span>
                          {hit.campaign.region && (
                            <span className="text-[11px] text-gray-500">• {hit.campaign.region}</span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 italic">{hit.snippet}</p>
                      </div>
                      <button
                        onClick={() => useAsSource(hit)}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Use as source
                      </button>
                    </div>

                    <div className="mt-3 flex items-center gap-4 text-[11px] text-gray-600">
                      {hit.campaign.kpis?.ctr && (
                        <span>CTR <strong className="text-gray-900">{hit.campaign.kpis.ctr}%</strong></span>
                      )}
                      {hit.campaign.kpis?.conversions && (
                        <span>Conv. <strong className="text-gray-900">{hit.campaign.kpis.conversions.toLocaleString()}</strong></span>
                      )}
                      {hit.campaign.kpis?.cpa && (
                        <span>CPA <strong className="text-gray-900">₹{hit.campaign.kpis.cpa}</strong></span>
                      )}
                      {(hit.campaign.reuses_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-violet-700">
                          <Recycle className="w-3 h-3" /> {hit.campaign.reuses_count} reuses
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignsSearch;
